import { Text, View } from "@react-pdf/renderer";
import { styles } from "./ReactPdfStyles";
import dayjs from "dayjs";

function ApprovalTimeSection({
	month,
	data,
	type,
}: {
	month: string;
	data: any;
	type: "project" | "individual";
}) {
	const numberOfDays = dayjs(month).daysInMonth();

	return (
		<View style={[styles.tableFontSize]}>
			<Text style={styles.tableHeader}>Approved Time</Text>

			<View
				style={[
					styles.row,
					{ borderLeft: "0.5px solid #A8A8A8", minHeight: 32 },
				]}
			>
				<View
					style={[
						styles.headerFill,
						styles.labelCell,
						{
							borderLeft: "none",
							justifyContent: "center",
							padding: 6,
							minWidth: 80,
							maxWidth: 80,
						},
					]}
				>
					<Text>{type === "project" ? "Role" : "Project"}</Text>
				</View>
				<View
					style={[
						styles.headerFill,
						styles.labelCell,
						{
							justifyContent: "center",
							padding: 6,
							minWidth: 80,
							maxWidth: 80,
						},
					]}
				>
					<Text>{type === "project" ? "Employee" : "Role"}</Text>
				</View>
				<View
					style={[
						styles.column,
						styles.headerFill,
						styles.borderLeft,
						{ flex: 1 },
					]}
				>
					<Text
						style={[
							{
								textAlign: "center",
								padding: 4,
								fontWeight: "bold",
							},
							styles.borderBottom,
						]}
					>
						Days
					</Text>
					<View style={[styles.row, styles.headerFill]}>
						{Array.from({ length: numberOfDays }).map((_, dayIndex) => {
							return (
								<Text
									key={dayIndex}
									style={{
										flex: 1,
										textAlign: "center",
										borderLeft: dayIndex == 0 ? "none" : "0.5px solid #A8A8A8",
										borderBottom: "0.5px solid #A8A8A8",
										padding: 2,
									}}
								>
									{dayIndex + 1}
								</Text>
							);
						})}
					</View>
				</View>
				<View
					style={[
						styles.hoursCell,
						styles.headerFill,
						{ justifyContent: "center", fontWeight: "bold" },
					]}
				>
					<Text>Hours</Text>
				</View>

				<View
					style={[
						styles.daysCell,
						styles.headerFill,
						{ justifyContent: "center", fontWeight: "bold" },
					]}
				>
					<Text>Days</Text>
				</View>
			</View>

			{data.map((item: any, index: number) => {
				const entries = item.roles || item.employees;
				const firstColumnItem = item.project || item.role;
				return (
					<>
						{entries.map((entry: any, entryIndex: number) => {
							const secondColumnItem = entry.role || entry.employee_name;
							return (
								<View
									key={index}
									style={[styles.row, { borderLeft: "0.5px solid #A8A8A8" }]}
									wrap={false}
								>
									{/* This View style should only have border bottom only if it's the last role */}
									{/* If its not the first role, add border top 0.5px solid #E9E9E9 */}
									<View
										key={index}
										style={[
											styles.headerFill,
											{
												borderTop:
													entryIndex === 0 ? "none" : "0.5px solid #E9E9E9",
												borderBottom:
													entryIndex === entries.length - 1
														? "0.5px solid #A8A8A8"
														: "1px solid #E9E9E9",
												padding: 6,
												minWidth: 80,
												maxWidth: 80,
											},
										]}
									>
										{/* show project if its the first role */}
										{entryIndex === 0 && <Text>{firstColumnItem}</Text>}
									</View>
									<View
										key={entryIndex}
										style={[
											styles.headerFill,
											{
												flex: 1,
												display: "flex",
												borderLeft: "0.5px solid #A8A8A8",
												borderBottom: "0.5px solid #A8A8A8",
												padding: 6,
												minWidth: 80,
												maxWidth: 80,
											},
										]}
									>
										<Text>{secondColumnItem}</Text>
									</View>
									{Array.from({ length: numberOfDays }).map((_, dayIndex) => {
										const day = entry.approved_time.find(
											(d: any) => dayjs(d.date).date() === dayIndex + 1
										);

										const isWeekend =
											dayjs(month)
												.date(dayIndex + 1)
												.format("dddd") === "Saturday" ||
											dayjs(month)
												.date(dayIndex + 1)
												.format("dddd") === "Sunday";

										return (
											<View
												key={dayIndex}
												style={[
													isWeekend ? styles.weekendCell : {},
													{
														flex: 1,
														textAlign: "center",
														borderLeft: "0.5px solid #A8A8A8",
														borderBottom: "0.5px solid #A8A8A8",
														padding: "6px 2px",
														alignItems: "center",
													},
												]}
											>
												<Text>{day?.duration.toFixed(2) ?? ""}</Text>
											</View>
										);
									})}
									<View style={styles.hoursCell}>
										<Text style={{ textAlign: "center" }}>
											{entry.total_approved_time.toFixed(2)}
										</Text>
									</View>

									<View style={styles.daysCell}>
										<Text style={{ textAlign: "center" }}>
											{(entry.total_approved_time / 8).toFixed(2)}
										</Text>
									</View>
								</View>
							);
						})}
					</>
				);
			})}

			<View
				style={[
					styles.row,
					{ justifyContent: "flex-end", minHeight: 32, marginBottom: 15 },
				]}
				wrap={false}
			>
				{/* Set min width to be total width - 240 (left and right part) and divide by numberOfDays then times 3 */}
				<Text
					style={[
						styles.headerFill,
						{
							textAlign: "center",
							padding: 6,
							borderLeft: "0.5px solid #A8A8A8",
							borderBottom: "0.5px solid #A8A8A8",
							minWidth: ((1148 - 240) / numberOfDays) * 3,
						},
					]}
				>
					Total
				</Text>
				<Text style={styles.hoursCell}>
					{data
						.flatMap((item: any) => item.roles || item.employees)
						.reduce(
							(acc: number, role: any) => acc + role.total_approved_time,
							0
						)
						.toFixed(2)}
				</Text>
				<Text style={styles.daysCell}>
					{(
						data
							.flatMap((item: any) => item.roles || item.employees)
							.reduce(
								(acc: number, role: any) => acc + role.total_approved_time,
								0
							) / 8
					).toFixed(2)}
				</Text>
			</View>
		</View>
	);
}

export default ApprovalTimeSection;
